import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { useLists } from "@/hooks/useLists";

const DEFAULT_SUP_CATS = ["caffetteria", "bevande", "birre", "vini", "liquori", "cucina", "freschi", "congelati", "dispensa", "pulizia", "packaging", "materiali di consumo"];

export default function Suppliers() {
  const { lists } = useLists();
  const CATS = lists?.supplier_categories?.length ? lists.supplier_categories : DEFAULT_SUP_CATS;
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", contact: "", phone: "", email: "", address: "", payment_terms: "", delivery_days: 2, min_order: 0, categories: [], reliability: 4, notes: "" });
  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const load = async () => { const { data } = await api.get("/suppliers"); setList(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/suppliers", { ...form, delivery_days: Number(form.delivery_days), min_order: Number(form.min_order), reliability: Number(form.reliability), categories: form.categories.filter(Boolean) }); toast.success("Fornitore aggiunto"); setOpen(false); load(); } catch { toast.error("Errore"); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/suppliers/${id}`); load(); };

  return (
    <div data-testid="suppliers-page">
      <PageHeader eyebrow="Fornitori" title="Rubrica fornitori"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-supplier-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-serif">Nuovo fornitore</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label><Input data-testid="sup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Ragione sociale</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div><Label>Referente</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Pagamento</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="30gg" /></div>
                <div><Label>Giorni consegna</Label><Input type="number" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} /></div>
                <div>
                  <Label>Lead time (giorni)</Label>
                  <Input type="number" min="0" value={form.lead_time_days || ""}
                    onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
                    placeholder="Tempo medio tra ordine e consegna"
                    data-testid="sup-lead-time" />
                </div>
                <div><Label>Minimo ordine (€)</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} /></div>
                <div><Label>Affidabilità (1-5)</Label><Input type="number" min="1" max="5" value={form.reliability} onChange={(e) => setForm({ ...form, reliability: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Categorie fornite</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {CATS.map((c) => (
                      <button type="button" key={c} onClick={() => toggleCat(c)}
                        data-testid={`sup-cat-${c}`}
                        className={`text-xs px-2.5 py-1 rounded-full border ${form.categories.includes(c) ? "bg-neutral-900 text-[#FDFBF7] border-neutral-900" : "bg-white border-[#E2D9C8] hover:border-neutral-900"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <DialogFooter className="col-span-2"><Button data-testid="sup-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} className="border border-[#E2D9C8] rounded-lg p-5 bg-white" data-testid={`sup-${s.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-serif text-xl">{s.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{s.company}</div>
              </div>
              <button onClick={() => del(s.id)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {s.contact && <div><span className="text-neutral-500">Ref:</span> {s.contact}</div>}
              {s.phone && <div><span className="text-neutral-500">Tel:</span> {s.phone}</div>}
              {s.email && <div><span className="text-neutral-500">Email:</span> {s.email}</div>}
              {s.payment_terms && <div><span className="text-neutral-500">Pag:</span> {s.payment_terms}</div>}
              <div><span className="text-neutral-500">Consegna:</span> {s.delivery_days}gg</div>
              <div><span className="text-neutral-500">Affidabilità:</span> {"★".repeat(s.reliability || 0)}<span className="text-neutral-300">{"★".repeat(5 - (s.reliability || 0))}</span></div>
            </div>
            {s.categories?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {s.categories.map((c) => <span key={c} className="text-xs px-2 py-0.5 bg-[#F3EDE1] rounded-full">{c}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
